import { isProfileV2, PermissionRules, PERMISSIONS, ROLES } from '@teamworksdev/influencer/core';
import { isAgent, isBrandBusiness, isBrandUser, isComplianceOfficer, isTeamUser } from './roles';

const hasExchangeWrite = isBrandUser;

const editAgreements = PermissionRules.or(
	hasExchangeWrite,
	isComplianceOfficer,
);

// Mirrors the legacy userStore.hasAgreementsAccess route check: a compliance
// user, or a brand user whose brand does NOT have the leaderboard enabled.
// (For a brand user, leaderboardEnabled === exchange_brand.leaderboard.is_enabled,
// which is profile-derived.)
const canAccessAgreements = PermissionRules.or(
	isComplianceOfficer,
	PermissionRules.and(
		isBrandUser,
		PermissionRules.custom((profile) =>
			isProfileV2(profile)
			&& profile.role_id === ROLES.BRAND_USER
			&& !profile.data.exchange_brand.leaderboard.is_enabled,
		),
	),
);

const accessBulletins = PermissionRules.or(
	isBrandBusiness,
	isAgent,
);

const accessTransactionsPermissions = [
	PERMISSIONS.TRANSACTIONS_READ,
	PERMISSIONS.TRANSACTIONS_WRITE,
];
const accessTransactions = PermissionRules.or(
	PermissionRules.hasAnyRole([
		ROLES.BRAND_USER,
		ROLES.COMPLIANCE_OFFICER,
	]),
	PermissionRules.hasAnyTeamPermission(accessTransactionsPermissions),
	PermissionRules.hasAnyAgentPermission(accessTransactionsPermissions),
);

// Mirrors the legacy ProfileAccessService.canAccessTransactions route check:
// any agent, brand or compliance user, or a team user holding a transactions
// permission. (Broader than accessTransactions, which gates agents on a
// transactions permission too — kept distinct to preserve route behaviour.)
const canAccessTransactions = PermissionRules.or(
	isAgent,
	isBrandUser,
	isComplianceOfficer,
	PermissionRules.hasAnyTeamPermission(accessTransactionsPermissions),
);

const canReportTransactions = PermissionRules.or(
	PermissionRules.hasAgentPermission(PERMISSIONS.TRANSACTIONS_WRITE),
	PermissionRules.hasTeamPermission(PERMISSIONS.TRANSACTIONS_WRITE),
);

const hasTransactionWrite = PermissionRules.hasTeamPermission(PERMISSIONS.TRANSACTIONS_WRITE);

const updateAgreementStatuses = PermissionRules.or(
	isAgent,
	isTeamUser,
	hasExchangeWrite,
);

const updateTransactionStatuses = PermissionRules.or(
	isAgent,
	isTeamUser,
	hasExchangeWrite,
);

const createConversations = PermissionRules.or(
	isBrandUser,
	isBrandBusiness,
);

const hasLeaderboardRead = PermissionRules.or(
	PermissionRules.hasAnyAgentPermission([
		PERMISSIONS.LEADERBOARD_READ,
	]),
	PermissionRules.hasAnyTeamPermission([
		PERMISSIONS.LEADERBOARD_READ,
		PERMISSIONS.LEADERBOARD_WRITE,
	]),
);

const accessTasks = PermissionRules.or(
	PermissionRules.hasAnyTeamPermission([
		PERMISSIONS.TRANSACTIONS_READ,
		PERMISSIONS.TRANSACTIONS_WRITE,
	]),
	PermissionRules.hasAnyAgentPermission([
		PERMISSIONS.TRANSACTIONS_READ,
		PERMISSIONS.TRANSACTIONS_WRITE,
	]),
	PermissionRules.hasAnyRole([
		ROLES.COMPLIANCE_OFFICER,
		ROLES.BRAND_USER,
	]),
);

const canUpdateTaskStatus = PermissionRules.hasAnyRole([
	ROLES.TEAM_USER,
	ROLES.BRAND_USER,
	ROLES.AGENT,
]);

export {
	accessBulletins,
	accessTransactions,
	canAccessTransactions,
	canAccessAgreements,
	accessTasks,
	canReportTransactions,
	canUpdateTaskStatus,
	editAgreements,
	hasExchangeWrite,
	hasTransactionWrite,
	updateAgreementStatuses,
	updateTransactionStatuses,
	createConversations,
	hasLeaderboardRead,
};
